import { memo, useCallback, useEffect, useRef } from 'react';
import { useSignal } from '@preact/signals-react';
import { DropDownWrapper, MenuItem } from 'components';
import { ArrowBack, ArrowDropDown } from 'assets/icons';
import { CountrySelect } from './CountrySelect/CountrySelect';
import * as S from './MetricCardHeader.styled';

// TYPES:
// 'Applications'
// 'ApplicationsPublisher'
// 'Campaigns'
// 'Events/Installs Ratio'
// 'Top Events'

function capitaliseStr(str) {
  if (!str) return '';
  if (str === 'eCPM') return str;

  const newStr = str;
  return newStr[0].toUpperCase() + newStr.slice(1);
}

function getDisplayType(type) {
  if (type === 'ApplicationsPublisher') return 'Applications';
  return type;
}

function noop() {}

export const MetricCardHeader = memo(
  ({
    type,
    metrics,
    events,
    isTextIcons,
    countries,
    selectedCountry,
    selectedMetric,
    selectedEventInd,
    sortDirection,
    onMetricChange,
    onCountryChange,
    onEventChange = noop,
    onSortChange,
  }) => {
    const eventSelectRef = useRef(null);
    const metricSelectRef = useRef(null);
    const eventDropDownIsOpen$ = useSignal(false);
    const metricDropDownIsOpen$ = useSignal(false);

    const closeOnOutsideClick = useCallback(
      (event) => {
        if (
          eventSelectRef.current?.contains(event.target) ||
          metricSelectRef.current?.contains(event.target)
        )
          return;

        eventDropDownIsOpen$.value = false;
        metricDropDownIsOpen$.value = false;
      },
      [eventDropDownIsOpen$, metricDropDownIsOpen$],
    );

    const toggleEventDropDown = useCallback(() => {
      eventDropDownIsOpen$.value = !eventDropDownIsOpen$.value;
    }, [eventDropDownIsOpen$]);

    const closeEventDropDown = useCallback(() => {
      eventDropDownIsOpen$.value = false;
    }, [eventDropDownIsOpen$]);

    const toggleMetricDropDown = useCallback(() => {
      metricDropDownIsOpen$.value = !metricDropDownIsOpen$.value;
    }, [metricDropDownIsOpen$]);

    const closeMetricDropDown = useCallback(() => {
      metricDropDownIsOpen$.value = false;
    }, [metricDropDownIsOpen$]);

    const handleSortDesc = useCallback(() => {
      onSortChange('desc');
    }, [onSortChange]);

    const handleSortAsc = useCallback(() => {
      onSortChange('asc');
    }, [onSortChange]);

    const handleEventChange = useCallback(
      (value) => {
        onEventChange(value);
        eventDropDownIsOpen$.value = false;
      },
      [eventDropDownIsOpen$, onEventChange],
    );

    const handleMetricChange = useCallback(
      (value) => {
        onMetricChange(value);
        metricDropDownIsOpen$.value = false;
      },
      [metricDropDownIsOpen$, onMetricChange],
    );

    useEffect(() => {
      document.addEventListener('click', closeOnOutsideClick, true);
      return () => document.removeEventListener('click', closeOnOutsideClick, true);
    }, [closeOnOutsideClick]);

    return (
      <S.Wrap>
        <S.Title>{getDisplayType(type)}</S.Title>
        <S.Controls>
          <S.SortGroup>
            <S.SortButton
              type='button'
              isText={isTextIcons}
              isActive={sortDirection === 'desc'}
              onClick={handleSortDesc}
              aria-label={isTextIcons ? 'Best' : 'Sort descending'}
            >
              {isTextIcons ? (
                'Best'
              ) : (
                <ArrowBack color='currentColor' style={{ transform: 'rotate(90deg)' }} />
              )}
            </S.SortButton>
            <S.SortButton
              type='button'
              isText={isTextIcons}
              isActive={sortDirection === 'asc'}
              onClick={handleSortAsc}
              aria-label={isTextIcons ? 'Worst' : 'Sort ascending'}
            >
              {isTextIcons ? (
                'Worst'
              ) : (
                <ArrowBack color='currentColor' style={{ transform: 'rotate(-90deg)' }} />
              )}
            </S.SortButton>
          </S.SortGroup>
          {type === 'Events/Installs Ratio' && (
            <>
              <S.SelectWrap ref={eventSelectRef}>
                <S.SelectButton type='button' onClick={toggleEventDropDown}>
                  <S.SelectValue>{events[selectedEventInd]}</S.SelectValue>
                  <S.SelectIcon>
                    <ArrowDropDown />
                  </S.SelectIcon>
                </S.SelectButton>
                <DropDownWrapper
                  isOpen$={eventDropDownIsOpen$}
                  width={180}
                  targetRef={eventSelectRef}
                  style={{ top: '36px', right: '0', maxHeight: '260px', overflowY: 'auto' }}
                  outSideClickAction={closeEventDropDown}
                >
                  {events.map((event, ind) => (
                    <MenuItem
                      key={event + ind}
                      value={ind}
                      height={32}
                      isSelected={ind === selectedEventInd}
                      onClick={handleEventChange}
                    >
                      {event}
                    </MenuItem>
                  ))}
                </DropDownWrapper>
              </S.SelectWrap>
              <p>vs.</p>
            </>
          )}
          {countries && type === 'ApplicationsPublisher' && (
            <CountrySelect
              countries={countries}
              selectedCountry={selectedCountry}
              onCountryChange={onCountryChange}
            />
          )}
          {type !== 'Top Events' && (
            <S.SelectWrap ref={metricSelectRef} style={{ flexShrink: '0' }}>
              <S.SelectButton
                type='button'
                onClick={toggleMetricDropDown}
                style={{ width: type === 'ApplicationsPublisher' ? '100px' : '90px' }}
              >
                <S.SelectValue>{capitaliseStr(selectedMetric)}</S.SelectValue>
                <S.SelectIcon>
                  <ArrowDropDown />
                </S.SelectIcon>
              </S.SelectButton>
              <DropDownWrapper
                isOpen$={metricDropDownIsOpen$}
                width={120}
                targetRef={metricSelectRef}
                style={{ top: '36px', right: '0' }}
                outSideClickAction={closeMetricDropDown}
              >
                {metrics.map((metric) => (
                  <MenuItem
                    key={metric}
                    value={metric}
                    height={32}
                    style={{ minWidth: 'unset' }}
                    isSelected={metric === selectedMetric}
                    onClick={handleMetricChange}
                  >
                    {capitaliseStr(metric)}
                  </MenuItem>
                ))}
              </DropDownWrapper>
            </S.SelectWrap>
          )}
        </S.Controls>
      </S.Wrap>
    );
  },
);

MetricCardHeader.displayName = 'MetricCardHeader';
